import React from 'react'
import { CollectionListItem, CollectionListItemTitle, CollectionListItemAction } from '@mytours/components'
import classNames from 'classnames'
import './styles.less'
import { ItemFavouriteButton } from '../favourite/ItemFavouriteButton'
import { AppMetaData, CollectionItem } from 'mytours-read-common'
import strings from '../../../../strings'

interface Props extends React.HTMLAttributes<HTMLElement> {
	variant?: 'wide' | 'grid'
	label?: React.ReactNode
	image?: string
	image2x?: string
	alt?: string
	loading?: boolean
	itemId: number
	itemType: string
	item: CollectionItem
	toggleFavorite?: React.MouseEventHandler<HTMLButtonElement>
	isFavourited?: boolean
	// Cards can render before app metadata loads.
	appMetaData?: AppMetaData
	// Hide the favourite toggle (e.g. a read-only Purchased Content shelf). Defaults to shown.
	showFavourite?: boolean
	// Offline, and this one was not saved. Dimmed and labelled rather than hidden: content that vanishes
	// reads as a broken app, and the card still opens — whatever is cached of it still shows.
	unavailableOffline?: boolean
}

const CardImage: React.FC<Props> = ({
	variant = 'grid',
	label,
	image,
	image2x,
	alt,
	className,
	loading,
	// Destructured only to keep them out of `...rest` — an <article> can't take these as DOM attributes.
	itemId: _itemId,
	itemType: _itemType,
	item: _item,
	toggleFavorite,
	isFavourited,
	appMetaData,
	showFavourite = true,
	unavailableOffline = false,
	role,
	onClick,
	...rest
}) => {
	// Skip empty headings when translated titles are missing or blank.
	const hasLabel = label !== undefined && label !== null && label !== ''

	return (
		<CollectionListItem
			variant={variant}
			loading={loading}
			src={image}
			srcSet={image2x ? `${image2x} 2x` : undefined}
			alt={alt || ''}
			className={classNames('card-image', className, { 'card-image-offline': unavailableOffline })}
			{...rest}
			// A placeholder has no item behind it yet, so it gets neither the role nor the handler:
			// the title it would be named by comes from that item, leaving an unnamed control that
			// does nothing when activated.
			{...(loading ? {} : { role, onClick })}
		>
			{/* No handler means a read-only consumer — don't render a heart button that can't toggle. */}
			{showFavourite && toggleFavorite && (
				<CollectionListItemAction>
					<ItemFavouriteButton
						onClick={toggleFavorite}
						isFavourited={isFavourited}
						favoritesEnabled={appMetaData?.favorites_enabled}
					/>
				</CollectionListItemAction>
			)}
			{unavailableOffline && <span className="card-image-offline-chip">{strings.available_online}</span>}
			{hasLabel && <CollectionListItemTitle>{label}</CollectionListItemTitle>}
		</CollectionListItem>
	)
}

export default CardImage
